import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  makeStyles,
  Badge,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DelteIcon from '@material-ui/icons/Delete';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PaidIcon from '@material-ui/icons/Paid';
import EnhancedTableHead from '../EnhancedTableHead';

const useStyles = makeStyles({
  tableCell: {
    cursor: 'pointer',
  },
  loader: {
    position: 'absolute !important',
    top: '20px',
    left: '45%',
  },
  tableBody: {
    position: 'relative',
    height: '60px',
  },
});

const headCells = [
  { id: 'no', numeric: false, disablePadding: false, label: 'No' },
  {
    id: 'firstname',
    numeric: false,
    disablePadding: false,
    label: 'First Name',
    sortable: true,
  },
  {
    id: 'lastname',
    numeric: false,
    disablePadding: false,
    label: 'Last Name',
    sortable: true,
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email Address',
    sortable: true,
  },
  {
    id: 'company_vat',
    numeric: false,
    disablePadding: false,
    label: 'Company VAT',
    sortable: true,
  },
  {
    id: 'phonenumber',
    numeric: false,
    disablePadding: false,
    label: 'Phone Number',
    sortable: true,
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
    sortable: true,
  },
  {
    id: 'payment_failed',
    numeric: false,
    disablePadding: false,
    label: 'Nr of failed payments',
    sortable: true,
  },
  {
    id: 'creation_date',
    numeric: false,
    disablePadding: false,
    label: 'Date Added',
    sortable: true,
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: 'Action',
    sortable: false,
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const ClientsListResults = ({
  clients,
  handleRow,
  handleToggleActive,
  loading,
  pagerInfo,
  handlePagerInfo,
  onSortChange,
  ...rest
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('creation_date');
  const navigate = useNavigate();

  const handleLimitChange = (event) => {
    handlePagerInfo({
      limit: event.target.value,
      page: 1,
      totalSize: 0,
    });
  };

  const handlePageChange = (event, newPage) => {
    handlePagerInfo({
      ...pagerInfo,
      page: newPage + 1,
    });
  };

  const goToEdit = (id) => {
    navigate(`/app/clients/edit/${id}`, { state: id });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    if (onSortChange) {
      onSortChange(property, newOrder);
    }
  };

  const goToFailedPayments = (id) => {
    navigate(`/app/clients/failed_payments/${id}`, { state: id });
  };

  return (
    <Card {...rest}>
      <PerfectScrollbar>
        <Box sx={{ minWidth: 1050 }}>
          <Table>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody className={classes.tableBody}>
              {loading && <CircularProgress className={classes.loader} />}
              {!loading &&
                clients &&
                clients.map(
                  (user, index) => (
                    <TableRow hover key={user.id}>
                      <TableCell>
                        {(pagerInfo.page - 1) * pagerInfo.limit + index + 1}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                          }}
                        >
                          <Typography color="textPrimary" variant="body1">
                            {user.firstname}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.lastname}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.company_vat || '—'}</TableCell>
                      <TableCell>{user.phonenumber}</TableCell>
                      <TableCell>{user.status.toUpperCase()}</TableCell>
                      {user.payment_failed !== '0' ? (
                        <TableCell>
                          <Badge
                            color="secondary"
                            badgeContent={user.payment_failed}
                            style={{ cursor: 'pointer' }}
                            onClick={() => goToFailedPayments(user.id)}
                          >
                            <PaidIcon />
                          </Badge>
                        </TableCell>
                      ) : (
                        <TableCell>
                          <>
                            <Badge
                              color="secondary"
                              badgeContent={0}
                              style={{ cursor: 'pointer' }}
                              onClick={() => goToFailedPayments(user.id)}
                            >
                              <PaidIcon />
                            </Badge>
                          </>
                        </TableCell>
                      )}
                      <TableCell>
                        {user.creation_date
                          ? new Date(user.creation_date).toLocaleDateString('pt-PT')
                          : '-'}
                      </TableCell>
                      <TableCell>
                            <>
                            <EditIcon
                              style={{ cursor: 'pointer' }}
                              onClick={() => goToEdit(user.id)}
                            />
                            </>
                        {handleToggleActive && (
                          user.status === 'inactive' ? (
                            <CheckCircleOutlineIcon
                              titleAccess="Reactivate"
                              style={{ cursor: 'pointer', color: 'green', marginLeft: 8 }}
                              onClick={() => handleToggleActive(user.id, user.status)}
                            />
                          ) : (
                            <BlockIcon
                              titleAccess="Deactivate"
                              style={{ cursor: 'pointer', color: '#cc7a00', marginLeft: 8 }}
                              onClick={() => handleToggleActive(user.id, user.status)}
                            />
                          )
                        )}
                        {user.status !== 'inactive' && (
                          <>
                            <DelteIcon
                              style={{ cursor: 'pointer', color: 'indianred', marginLeft: 8 }}
                              onClick={() => handleRow(user.id)}
                            />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                )}
              {!loading && !clients && (
                <Typography className={classes.loader}>No Data</Typography>
              )}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={pagerInfo ? pagerInfo.totalSize : 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={pagerInfo ? pagerInfo.page - 1 : 0}
        rowsPerPage={pagerInfo ? pagerInfo.limit : 0}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
    </Card>
  );
};

ClientsListResults.propTypes = {
  clients: PropTypes.array,
  handleRow: PropTypes.func,
  loading: PropTypes.bool,
  pagerInfo: PropTypes.object,
  handlePagerInfo: PropTypes.func,
};

export default ClientsListResults;
